import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wrench,
  ArrowLeft,
  User,
  MapPin,
  Calendar as CalendarIcon,
  Clock,
  FileText,
  Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import * as crmBackend from '../services/crmBackendService';
import axios from 'axios';

const { TIME_WINDOWS } = crmBackend;

const TechCreateJob = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const API_BASE = useMemo(() => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, ''), []);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [fetchingCustomer, setFetchingCustomer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [addrQuery, setAddrQuery] = useState('');
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const skipAddrSuggest = React.useRef(false);

  // Commercial Customers & Multiple Service Locations, Tech App integration
  // (2026-08-12). `customerLocations` is this customer's full saved set
  // (from GET .../service-locations); `additionalLocations` is what the
  // tech has picked for THIS job so far -- staged client-side, since the
  // Estimate doesn't exist yet at this point in the flow. Submitted once,
  // alongside the primary address, in handleSubmit below. The existing
  // Primary Service Location (the "Service Address" card further down)
  // is intentionally untouched by this feature.
  const [customerLocations, setCustomerLocations] = useState([]);
  const [additionalLocations, setAdditionalLocations] = useState([]);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [newLocationOpen, setNewLocationOpen] = useState(false);
  const [newLocationForm, setNewLocationForm] = useState({
    location_name: '', address_line1: '', city: '', state: '', zip: '',
  });
  const [savingLocation, setSavingLocation] = useState(false);

  const [formData, setFormData] = useState({
    customer: '',
    customer_phone: '',
    customer_email: '',
    service_type: '',
    scheduled_date: '',
    scheduled_time: '8AM-11AM',
    priority: 'Medium',
    description: '',
    address: '',
    // Structured components alongside the flattened `address` string above —
    // additive, not a replacement. `address` still drives the visible field
    // and custom_service_address (used elsewhere); these three are what the
    // 2026-08-12 audit found were being read from both address sources
    // below (Google Places and an existing customer's stored address) and
    // then immediately discarded before ever reaching the backend.
    city: '',
    state: '',
    postal_code: '',
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    const q = addrQuery.trim();
    if (skipAddrSuggest.current) { skipAddrSuggest.current = false; return; }
    if (q.length < 4) { setAddrSuggestions([]); setAddrOpen(false); return; }
    setAddrLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API_BASE}/api/address/suggest`, { query: q });
        setAddrSuggestions(data?.suggestions || []);
        setAddrOpen(true);
      } catch {
        setAddrSuggestions([]);
      } finally {
        setAddrLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [addrQuery, API_BASE]);

  const selectSuggestion = async (s) => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/address/details`, { params: { place_id: s.place_id, description: s.description } });
      setFormData((f) => ({
        ...f,
        address: data.address_line1 ?? s.description,
        city: data.city || '',
        state: data.state || '',
        postal_code: data.postal_code || '',
      }));
      setAddrQuery(s.description);
      setAddrOpen(false);
      setAddrSuggestions([]);
    } catch {
      // Details lookup failed — we only have the raw suggestion text, no
      // structured components. Clear any stale city/state/postal_code from
      // a previous selection rather than leaving them mismatched with this
      // new address.
      setFormData((f) => ({ ...f, address: s.description, city: '', state: '', postal_code: '' }));
      setAddrQuery(s.description);
      setAddrOpen(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const result = await crmBackend.getCustomers();
      setCustomers(result.data || []);
    } catch (err) {
      console.error('Failed to load customers', err);
    }
  };

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers.slice(0, 50);
    const digits = q.replace(/\D/g, '');
    return customers.filter(c => {
      if (c.name?.toLowerCase().includes(q)) return true;
      if (digits && (c.phone || '').replace(/\D/g, '').includes(digits)) return true;
      return false;
    }).slice(0, 20);
  }, [customers, customerSearch]);

  const handleSelectCustomer = async (customer) => {
    setCustomerSearch(customer.name);
    setCustomerOpen(false);
    setSelectedCustomer(customer);
    setFormData(f => ({ ...f, customer: customer.id, customer_phone: customer.phone || '', customer_email: customer.email || '' }));
    // Switching customers invalidates any staged additional locations from
    // a previously-selected customer -- those Service Location IDs belong
    // to the OLD customer and must never carry over.
    setAdditionalLocations([]);
    setCustomerLocations([]);
    setLocationPickerOpen(false);
    setNewLocationOpen(false);
    if (customer.id) {
      crmBackend.getCustomerServiceLocations(customer.id)
        .then(locResult => setCustomerLocations(locResult.locations || []))
        .catch(() => setCustomerLocations([]));
      setFetchingCustomer(true);
      try {
        const result = await crmBackend.getCustomerDetail(customer.id);
        const d = result.data || {};
        const addrParts = [d.address_line1, d.address_line2, d.city, d.state, d.postal_code].filter(Boolean);
        if (addrParts.length) {
          const fullAddr = addrParts.join(', ');
          skipAddrSuggest.current = true;
          setAddrQuery(fullAddr);
          setFormData(f => ({
            ...f,
            address: fullAddr,
            city: d.city || '',
            state: d.state || '',
            postal_code: d.postal_code || '',
            customer_phone: d.phone || f.customer_phone,
            customer_email: d.email || f.customer_email,
          }));
        } else if (d.email) {
          setFormData(f => ({ ...f, customer_phone: d.phone || f.customer_phone, customer_email: d.email }));
        }
      } catch {
        // address stays blank — tech can type it
      } finally {
        setFetchingCustomer(false);
      }
    }
  };

  // Locations already staged as additional, or already showing as the
  // customer's default primary address, are excluded from the picker so
  // the same property can't be selected twice from this screen. (The
  // backend's before_save hook is still the real enforcement boundary —
  // this is just so the tech isn't offered a choice that would be
  // rejected; see TESTING.md's convention of never trusting the frontend
  // alone for this class of check.)
  const availableCustomerLocations = useMemo(
    () => customerLocations.filter(
      loc => !additionalLocations.some(a => a.service_location === loc.name)
    ),
    [customerLocations, additionalLocations]
  );

  const addAdditionalLocation = (loc) => {
    setAdditionalLocations(prev => (
      prev.some(l => l.service_location === loc.name) ? prev : [...prev, {
        service_location: loc.name,
        location_name: loc.location_name,
        address_line1: loc.address_line1,
        city: loc.city,
        state: loc.state,
        zip: loc.zip,
      }]
    ));
    setLocationPickerOpen(false);
  };

  const removeAdditionalLocation = (serviceLocationId) => {
    setAdditionalLocations(prev => prev.filter(l => l.service_location !== serviceLocationId));
  };

  const handleCreateServiceLocation = async () => {
    if (!formData.customer) return;
    if (!newLocationForm.location_name.trim() || !newLocationForm.address_line1.trim()) {
      toast({ title: 'Missing info', description: 'Location name and address are required.', variant: 'destructive' });
      return;
    }
    setSavingLocation(true);
    try {
      const result = await crmBackend.createCustomerServiceLocation(formData.customer, newLocationForm);
      const loc = {
        name: result.service_location_id,
        location_name: newLocationForm.location_name,
        address_line1: newLocationForm.address_line1,
        city: newLocationForm.city,
        state: newLocationForm.state,
        zip: newLocationForm.zip,
      };
      setCustomerLocations(prev => [...prev, loc]);
      addAdditionalLocation(loc);
      setNewLocationForm({ location_name: '', address_line1: '', city: '', state: '', zip: '' });
      setNewLocationOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast({
        title: 'Could not save location',
        description: typeof detail === 'string' ? detail.slice(0, 200) : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingLocation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const techUser = localStorage.getItem('techUser') ? JSON.parse(localStorage.getItem('techUser')) : {};
      // Deliberately no native `required` attribute on Service Type/Date/
      // Description (or Address, above) -- found live 2026-08-13: on this
      // installed-PWA Tech App, the browser's native constraint validation
      // silently blocks form submission with NO visible feedback at all
      // (confirmed: clicking submit with a required field empty fires zero
      // network requests and zero console output -- React's onSubmit never
      // even runs). Explicit checks + a toast per field, same pattern this
      // file already used for Address, so a tech always sees WHY nothing
      // happened instead of a dead button.
      if (!formData.customer) {
        toast({ title: 'Customer required', description: 'Please select a customer.', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      if (!formData.service_type) {
        toast({ title: 'Service type required', description: 'Please select a service type.', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      if (!formData.scheduled_date) {
        toast({ title: 'Date required', description: 'Please select a date.', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      if (!formData.description.trim()) {
        toast({ title: 'Description required', description: 'Please describe the work to be done.', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      const addr = addrQuery || formData.address;
      if (!addr.trim()) {
        toast({ title: 'Address required', description: 'Please enter the service address.', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      const result = await crmBackend.createEstimateFromCustomer(formData.customer, {
        address: addr,
        city: formData.city || '',
        state: formData.state || '',
        postal_code: formData.postal_code || '',
        email: formData.customer_email || '',
        date: formData.scheduled_date,
        time: formData.scheduled_time,
        service_type: formData.service_type,
        priority: formData.priority,
        description: formData.description,
        technician: techUser.email || '',
        additional_service_locations: additionalLocations.map(l => l.service_location),
      });

      if (result && (result.name || result.quotation_name)) {
        toast({
          title: 'Job Created',
          description: `Estimate ${result.name || result.quotation_name} created successfully`,
        });
        navigate('/tech/dashboard');
      } else {
        throw new Error(result?.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Create job error:', error);
      const detail = error.response?.data?.detail;
      const errMsg = typeof detail === 'string'
        ? detail.replace(/.*"message":\s*"([^"]+)".*/, '$1').slice(0, 200)
        : (error.message || 'Please check your connection and try again.');
      toast({
        title: 'Failed to create job',
        description: errMsg,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-green-700 text-white sticky top-0 z-50 shadow-lg">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/tech/dashboard')}
              className="flex items-center space-x-2 hover:bg-green-600 px-3 py-2 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="flex items-center space-x-2">
              <Plus className="w-6 h-6" />
              <span className="font-bold text-lg">Create Job</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Customer Selection */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <User className="w-5 h-5 text-green-600" />
                <span>Customer</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="relative">
                <Input
                  value={customerSearch}
                  onChange={e => { setCustomerSearch(e.target.value); setCustomerOpen(true); setSelectedCustomer(null); }}
                  onFocus={() => setCustomerOpen(true)}
                  onBlur={() => setTimeout(() => setCustomerOpen(false), 180)}
                  placeholder="Search by name or phone..."
                  className="h-12 text-base"
                />
                {customerOpen && filteredCustomers.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredCustomers.map(c => (
                      <button key={c.id} type="button"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => handleSelectCustomer(c)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0">
                        <p className="text-sm font-medium text-gray-900">{c.name}</p>
                        {c.phone && <p className="text-xs text-gray-500">{c.phone}</p>}
                        {c.city && <p className="text-xs text-gray-400">{[c.city, c.state].filter(Boolean).join(', ')}</p>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedCustomer && (
                <div className="text-sm text-gray-600 bg-gray-50 rounded-md px-3 py-2 space-y-0.5">
                  <p className="font-medium">{selectedCustomer.name}</p>
                  {formData.customer_phone && <p className="text-xs text-green-700">{formData.customer_phone}</p>}
                  {formData.customer_email && <p className="text-xs text-gray-500">{formData.customer_email}</p>}
                  {fetchingCustomer && <p className="text-xs text-gray-400">Loading info...</p>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Service Type */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <Wrench className="w-5 h-5 text-green-600" />
                <span>Service Type</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={formData.service_type}
                onValueChange={(value) => setFormData({...formData, service_type: value})}
              >
                <SelectTrigger className="h-12 text-base">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Installation">Installation</SelectItem>
                  <SelectItem value="Repair">System Repair</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                  <SelectItem value="Inspection">Inspection</SelectItem>
                  <SelectItem value="Emergency">Emergency Service</SelectItem>
                  <SelectItem value="Winterization">Winterization</SelectItem>
                  <SelectItem value="Spring Startup">Spring Startup</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Schedule */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <CalendarIcon className="w-5 h-5 text-green-600" />
                <span>Schedule</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <Input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                  className="h-12 text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Time Window</label>
                <div className="grid grid-cols-2 gap-2">
                  {TIME_WINDOWS.map(w => (
                    <button key={w} type="button"
                      onClick={() => setFormData(f => ({ ...f, scheduled_time: w }))}
                      className={`h-10 rounded-md text-sm font-medium border transition-colors ${
                        formData.scheduled_time === w
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}>
                      {w}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Priority */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg">Priority</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={formData.priority}
                onValueChange={(value) => setFormData({...formData, priority: value})}
              >
                <SelectTrigger className="h-12 text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Description */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <FileText className="w-5 h-5 text-green-600" />
                <span>Job Description</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe the work to be done..."
                rows={4}
                className="text-base"
              />
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="bg-white">
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-green-600" />
                <span>Service Address</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <Input
                  value={addrQuery || formData.address}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAddrQuery(v);
                    setFormData((f) => ({ ...f, address: v }));
                  }}
                  onFocus={() => { if (addrSuggestions.length) setAddrOpen(true); }}
                  onBlur={() => setTimeout(() => setAddrOpen(false), 150)}
                  placeholder="Start typing an address…"
                  className="h-12 text-base"
                />
                {(addrLoading || (addrOpen && addrSuggestions.length > 0)) && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg overflow-hidden">
                    {addrLoading && <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>}
                    {!addrLoading && addrSuggestions.map((s) => (
                      <button
                        key={s.place_id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectSuggestion(s)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        {s.description}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Additional Service Locations — commercial jobs spanning more
              than one property. Zero or more, not a fixed "second"
              location; the primary above is untouched. */}
          {selectedCustomer && (
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-lg flex items-center space-x-2">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <span>Additional Service Locations</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {additionalLocations.map((loc) => (
                  <div key={loc.service_location} className="flex items-start justify-between bg-gray-50 rounded-md px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{loc.location_name}</p>
                      <p className="text-xs text-gray-500">
                        {[loc.address_line1, loc.city, loc.state, loc.zip].filter(Boolean).join(', ')}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAdditionalLocation(loc.service_location)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium px-2 shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                ))}

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { setLocationPickerOpen(o => !o); setNewLocationOpen(false); }}
                    className="w-full h-11 rounded-md border border-dashed border-green-400 text-green-700 text-sm font-medium hover:bg-green-50 flex items-center justify-center space-x-1"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Service Location</span>
                  </button>
                  {locationPickerOpen && (
                    <div className="absolute z-40 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                      {availableCustomerLocations.length === 0 && (
                        <div className="px-3 py-3 text-sm text-gray-500">
                          No saved Service Locations for this customer yet.
                        </div>
                      )}
                      {availableCustomerLocations.map((loc) => (
                        <button
                          key={loc.name}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => addAdditionalLocation(loc)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0"
                        >
                          <p className="text-sm font-medium text-gray-900">{loc.location_name}</p>
                          <p className="text-xs text-gray-500">
                            {[loc.address_line1, loc.city, loc.state, loc.zip].filter(Boolean).join(', ')}
                          </p>
                        </button>
                      ))}
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { setLocationPickerOpen(false); setNewLocationOpen(true); }}
                        className="w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-green-50 font-medium"
                      >
                        + Create a new Service Location
                      </button>
                    </div>
                  )}
                </div>

                {newLocationOpen && (
                  <div className="border rounded-md p-3 space-y-2 bg-gray-50">
                    <Input
                      value={newLocationForm.location_name}
                      onChange={(e) => setNewLocationForm(f => ({ ...f, location_name: e.target.value }))}
                      placeholder="Location name (e.g. Warehouse B)"
                      className="h-10 text-sm"
                    />
                    <Input
                      value={newLocationForm.address_line1}
                      onChange={(e) => setNewLocationForm(f => ({ ...f, address_line1: e.target.value }))}
                      placeholder="Address"
                      className="h-10 text-sm"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        value={newLocationForm.city}
                        onChange={(e) => setNewLocationForm(f => ({ ...f, city: e.target.value }))}
                        placeholder="City"
                        className="h-10 text-sm"
                      />
                      <Input
                        value={newLocationForm.state}
                        onChange={(e) => setNewLocationForm(f => ({ ...f, state: e.target.value }))}
                        placeholder="State"
                        className="h-10 text-sm"
                      />
                      <Input
                        value={newLocationForm.zip}
                        onChange={(e) => setNewLocationForm(f => ({ ...f, zip: e.target.value }))}
                        placeholder="ZIP"
                        className="h-10 text-sm"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        type="button"
                        onClick={handleCreateServiceLocation}
                        disabled={savingLocation}
                        className="flex-1 h-10 bg-green-600 hover:bg-green-700 text-white text-sm"
                      >
                        {savingLocation ? 'Saving…' : 'Save Location'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setNewLocationOpen(false)}
                        className="h-10 text-sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full h-14 bg-green-600 hover:bg-green-700 text-white text-lg font-semibold"
          >
            {isLoading ? 'Creating Job...' : 'Create Job'}
          </Button>
        </form>
      </div>

      {/* Powered By Footer */}
      <div className="mt-8 py-6 border-t border-gray-200 text-center text-xs text-gray-500 bg-white">
        Powered by <span className="font-bold text-gray-900">Boatman Systems™</span>
      </div>
    </div>
  );
};

export default TechCreateJob;